import i18next from 'i18next';
import findIndex from 'lodash/findIndex';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import uniq from 'lodash/uniq';
import uniqBy from 'lodash/uniqBy';

import {
    SEGMENT_COLOR,
    SEGMENT_STROKE_COLOR,
} from 'common/components/maps/constants';
import { POLYGON_SEGMENT } from 'common/components/segment/constants';
import {
    getNewSolarModules,
    getPolygonCoordinates,
} from 'common/utils/helpers';

export const getQuantityEnabledModules = (solarModules) => {
    return solarModules.reduce(
        (acc, current) => (current.enabled ? acc + 1 : acc),
        0,
    );
};

export const buildGroups = (response) => {
    let restoredGroup = [];

    if (!Array.isArray(response)) return restoredGroup;

    const length = response.length;

    if (length === 0) return restoredGroup;

    for (let i = 0; i < length; i++) {
        const cell = response[i];

        if (cell.group === null || cell.row === null || cell.col === null)
            continue;

        if (!restoredGroup[cell.group]) restoredGroup[cell.group] = [];

        if (!restoredGroup[cell.group][cell.cell])
            restoredGroup[cell.group][cell.cell] = [];
        if (!restoredGroup[cell.group][cell.cell][cell.row])
            restoredGroup[cell.group][cell.cell][cell.row] = [];

        restoredGroup[cell.group][cell.cell][cell.row][cell.col] = cell;
    }

    return restoredGroup;
};

export const restoreGroups = (response) => {
    let restoredGroup = [];

    if (!Array.isArray(response)) return restoredGroup;

    const length = response.length;

    if (length === 0) return restoredGroup;

    for (let i = 0; i < length; i++) {
        const cell = response[i];

        if (cell.group === null || cell.row === null || cell.col === null)
            continue;

        if (!restoredGroup[cell.group]) restoredGroup[cell.group] = [];

        if (!restoredGroup[cell.group][cell.cell])
            restoredGroup[cell.group][cell.cell] = [];
        if (!restoredGroup[cell.group][cell.cell][cell.row])
            restoredGroup[cell.group][cell.cell][cell.row] = [];

        restoredGroup[cell.group][cell.cell][cell.row][cell.col] = {
            cell: cell.cell,
            col: cell.col,
            enable: cell.enabled,
            group: cell.group,
            id: cell.id,
            paths: cell.path,
            row: cell.row,
        };
    }

    return restoredGroup;
};

export const getBreadcrumbs = (handleClickBreadcrumb, offerInfoPatern) => [
    {
        action: () =>
            handleClickBreadcrumb(get(offerInfoPatern, 'contactId', null)),
        label: get(offerInfoPatern, 'contactName', null),
    },
    {
        action: () =>
            handleClickBreadcrumb(
                get(offerInfoPatern, 'contactId', null),
                get(offerInfoPatern, 'projectId', null),
            ),
        label: get(offerInfoPatern, 'projectName', null),
    },
];

export const getDictionaryById = (array) =>
    array.reduce((acc, current) => ({ ...acc, [current.id]: current }), {});

export const getSolarCharacterization = (solarCaracterization) => {
    const {
        azimuth,
        col_spacing,
        frame_col_spacing,
        frame_cols_number,
        frame_row_spacing,
        frame_rows_number,
        orientation,
        row_spacing,
        safe_zone,
        tilt,
    } = solarCaracterization;
    return {
        azimuth,
        col_spacing,
        frame_col_spacing,
        frame_cols_number,
        frame_row_spacing,
        frame_rows_number,
        orientation,
        row_spacing,
        safe_zone,
        tilt,
    };
};

export const getGroupSize = (group) => {
    let meta = { cells: 0, cols: 0, rows: 0 };

    if (!Array.isArray(group) || group.length === 0) return null;

    const sample = group;
    meta.cells = sample.length;
    meta.rows = sample[0].length;
    meta.cols = sample[0][0].length;

    return meta;
};

export const getBiggestColumnCardinality = (group) => {
    if (!Array.isArray(group) || group.length === 0) return 0;
    const values = group.map((row) => row.length);
    return Math.max(...uniq(values));
};

const normalizeGroup = (group) => {
    if (!Array.isArray(group) || group.length === 0) return [];

    const biggestColumnCardinality = getBiggestColumnCardinality(group);

    const normalizedGroup = group.map((row) => {
        const newRow = [...row];
        if (newRow.length < biggestColumnCardinality) {
            const delta = biggestColumnCardinality - newRow.length;
            for (let i = 0; i < delta; i++) newRow.push(null);
        }
        return newRow;
    });

    return normalizedGroup;
};

export const mergeGroupCells = (group = [], index = 0) => {
    if (!Array.isArray(group) || group.length === 0) return null;
    const { rows: rowsSize } = getGroupSize(group);
    let merged = new Array(rowsSize).fill([]);
    for (let cells = 0; cells < group.length; cells++) {
        const cell = group[cells];
        for (let rows = 0; rows < cell.length; rows++) {
            const row = cell[rows];
            for (let cols = 0; cols < row.length; cols++) {
                const col = row[cols];
                merged[rows] = merged[rows].concat({
                    ...col,
                    cell: cells,
                    group: index,
                });
            }
        }
    }
    return merged;
};

const getColsToSlice = (group) => {
    let colsToSlice = new Set();

    if (!Array.isArray(group) || group.length === 0) return colsToSlice;

    const numRows = group.length;
    let tempCount = {};

    for (let r = 0; r < numRows; r++) {
        const row = group[r];
        for (let c = 0; c < row.length; c++) {
            const col = row[c];
            if (col?.enable) continue;
            if (!tempCount[c]) tempCount[c] = 0;
            tempCount[c]++;

            if (tempCount[c] === numRows) colsToSlice.add(c);
        }
    }

    return colsToSlice;
};

export const getModulesGroup = (group) => {
    if (!Array.isArray(group) || group.length === 0) return [];

    const normalizedGroup = normalizeGroup(group);
    const colsToSlice = getColsToSlice(normalizedGroup);

    if (!colsToSlice.size) return [normalizedGroup];

    const numRows = normalizedGroup.length;
    const numCols = normalizedGroup[0].length;

    let result = [];
    let tempMatrix = [];

    for (let cols = 0; cols < numCols; cols++) {
        if (colsToSlice.has(cols)) {
            if (tempMatrix.length) result.push(tempMatrix);
            tempMatrix = [];
            continue;
        }

        for (let rows = 0; rows < numRows; rows++) {
            if (!tempMatrix[rows]) tempMatrix[rows] = [];
            const value = normalizedGroup[rows][cols];
            tempMatrix[rows].push(value);
        }
    }

    if (tempMatrix.length) result.push(tempMatrix);

    return result;
};

export const getModulesGroups = (groups) => {
    const finalGroups = [];

    if (!Array.isArray(groups)) return finalGroups;

    for (let g = 0; g < groups.length; g++) {
        const mergedGroupCells = mergeGroupCells(groups[g], g);
        const newGroups = getModulesGroup(mergedGroupCells);

        for (let j = 0; j < newGroups.length; j++) {
            finalGroups.push(newGroups[j]);
        }
    }

    return finalGroups;
};

export const formatSegment = (segment, parentId) => {
    const {
        custom_inputs,
        field_segment_points,
        groups,
        height,
        id,
        image,
        name,
        opacity = 100,
        panel = null,
        panel_modules,
        rotate = 0,
        segment_errors,
        solar_characterization,
        system,
        total_area,
        type,
        zindex,
    } = segment;

    const solarCharacterization = getSolarCharacterization(
        solar_characterization,
    );

    const solarModules = getNewSolarModules(panel_modules);

    const quantityEnabledModules = getQuantityEnabledModules(solarModules);

    const grouped = restoreGroups(solarModules);
    const modules_groups = getModulesGroups(grouped);

    let brandName = '';
    let is_custom_module = false;
    let panelConfig = null;
    let selectedModule = null;
    let solar_module = null;
    let segment_color = SEGMENT_COLOR;
    let segment_stroke_color = SEGMENT_STROKE_COLOR;

    if (panel) panelConfig = panel;

    if (panelConfig) {
        if (!panelConfig.is_custom) {
            solar_module = panel.id;
            brandName = panel.brand.name;
        } else {
            is_custom_module = true;
            brandName = 'custom';
        }

        panelConfig = {
            ...panelConfig,
            brandName: panel.brand.name,
            watts: panelConfig.max_power,
        };

        selectedModule = panelConfig;
    }

    const enableModules = solarModules.filter((item) => item.enabled);

    return {
        ...custom_inputs,
        ...solarCharacterization,
        brandName,
        custom_inputs: custom_inputs,
        gclamp_type: custom_inputs?.gclamp_type || '',
        grouped_cells: grouped,
        groups,
        height,
        id,
        image,
        is_custom_module,
        modules_groups,
        name,
        opacity,
        open: false,
        panel: panelConfig,
        parent_field_segment: parentId,
        polygon: getPolygonCoordinates(field_segment_points),
        prev_location: getPolygonCoordinates(field_segment_points),
        quantity_enabled_modules: quantityEnabledModules,
        rotate,
        segment_color,
        segment_errors,
        segment_stroke_color,
        selectedModule,
        solar_module,
        solar_modules: solarModules,
        solar_modules_number: enableModules?.length || 0,
        system: system?.id,
        system_uid: system?.uniqueid,
        system_name: system?.name,
        total_area,
        type,
        type_application: system?.type_application,
        zIndex: zindex,
    };
};

export const getParentFieldSegments = (segments, parentId) => {
    let newSegments = [];
    let panels = [];
    if (!segments.length) return { newSegments, panels };

    newSegments = segments.map((segment) => {
        const formatedSegment = formatSegment(segment, parentId);
        if (segment.panel && segment.type === POLYGON_SEGMENT)
            panels.push(segment.panel);
        return formatedSegment;
    });

    panels = uniqBy(panels, 'id');

    return { newSegments, panels };
};

export const handleOpenChangeSegments = ({
    item,
    segments,
    updateSegments,
}) => {
    const newValues = segments.map((segment) =>
        segment.id === item.id
            ? { ...segment, open: !segment.open }
            : { ...segment, open: false },
    );
    updateSegments(newValues);
};

export const handleOpenDrawer = (swipeableDrawerRef, value) => {
    if (swipeableDrawerRef.current) {
        const { handleOpen } = swipeableDrawerRef.current;
        handleOpen(value);
    }
};

export const checkCallbackError = (id, values, mapRef) => {
    if (!mapRef.current) return;
    mapRef.current.resetSegmentPosition(id, values);
};

export const checkCallback = (id, modules, mapRef) => {
    if (!mapRef.current) return;
    mapRef.current.setModulesToSegment(id, modules);
};

export const mergeArrays = (arr1 = [], arr2 = []) => {
    let merge = [...arr1, ...arr2];
    let newArray = [];
    let uniqueObject = {};

    for (let i in merge) {
        const id = merge[i]['id'];
        uniqueObject[id] = merge[i];
    }

    for (let i in uniqueObject) {
        newArray.push(uniqueObject[i]);
    }

    return newArray;
};

export const prepareModulesGroups = (groups) => {
    let newGroups = [];

    for (let i = 0; i < groups.length; i++) {
        const row = groups[i].length;
        const col = groups[i][0].length;

        const index = findIndex(newGroups, { row, col });

        if (index === -1) {
            newGroups.push({ col, match: 1, row });
        } else {
            newGroups[index].match = newGroups[index].match + 1;
        }
    }

    return newGroups;
};

export const markGroupEdges = (groups) => {
    let newGroups = Array(groups.length).fill(null);
    for (let index = 0; index < groups.length; index++) {
        const group = groups[index];
        const maxColsCount = getBiggestColumnCardinality(group);
        const marked = group.map((row) => {
            return row.map((col, colIndex) => {
                const isEdge = colIndex === 0 || colIndex === maxColsCount - 1;
                return { ...col, is_edge: isEdge };
            });
        });

        newGroups[index] = marked;
    }

    return newGroups;
};

export function splitMatrix(matrix) {
    if (!Array.isArray(matrix) || !matrix.length || !Array.isArray(matrix[0])) {
        return [];
    }

    const rows = matrix.length;
    const cols = matrix[0].length;

    // Find rows with all zeros
    const zeroRows = new Set();
    for (let i = 0; i < rows; i++) {
        if (matrix[i].every(({ enable }) => enable === false)) {
            zeroRows.add(i);
        }
    }

    // Find columns with all zeros
    const zeroCols = new Set();
    for (let j = 0; j < cols; j++) {
        let allZeros = true;
        for (let i = 0; i < rows; i++) {
            if (matrix[i][j].enable !== false) {
                allZeros = false;
                break;
            }
        }
        if (allZeros) {
            zeroCols.add(j);
        }
    }

    // Find row segments between zero rows
    const rowSegments = [];
    let startRow = 0;
    for (let i = 0; i <= rows; i++) {
        if (i === rows || zeroRows.has(i)) {
            if (startRow < i) {
                rowSegments.push([startRow, i]);
            }
            startRow = i + 1;
        }
    }

    // Find column segments between zero columns
    const colSegments = [];
    let startCol = 0;
    for (let j = 0; j <= cols; j++) {
        if (j === cols || zeroCols.has(j)) {
            if (startCol < j) {
                colSegments.push([startCol, j]);
            }
            startCol = j + 1;
        }
    }

    // Create submatrices
    const result = [];
    for (const [startRow, endRow] of rowSegments) {
        for (const [startCol, endCol] of colSegments) {
            const submatrix = [];
            for (let i = startRow; i < endRow; i++) {
                const row = [];
                for (let j = startCol; j < endCol; j++) {
                    row.push(matrix[i][j]);
                }
                submatrix.push(row);
            }
            result.push(submatrix);
        }
    }

    return result;
}

export const getModulesGroupV2 = (groups) => {
    const finalGroups = [];

    if (!Array.isArray(groups)) return finalGroups;

    for (let g = 0; g < groups.length; g++) {
        const mergedGroupCells = mergeGroupCells(groups[g], g);
        const newGroups = splitMatrix(mergedGroupCells);

        for (let j = 0; j < newGroups.length; j++) {
            finalGroups.push(newGroups[j]);
        }
    }

    return finalGroups;
};

export function validateGroupsDimensions({
    groups,
    maxCols,
    maxRows,
    minCols,
    minRows,
} = {}) {
    return groups.map((group) => {
        const { row, col } = group;
        if (row < minRows || row > maxRows || col < minCols || col > maxCols)
            return { ...group, valid: { dimension: false } };
        return { ...group, valid: { dimension: true } };
    });
}

export const escapeUseEffect = (
    setDrawingMode,
    setIsDisablingModules,
    useCallback,
) => {
    const escFunction = useCallback((event) => {
        if (event.key === 'Escape') {
            setDrawingMode(false);
            setIsDisablingModules(false);
        }
    }, []);

    return () => {
        document.addEventListener('keydown', escFunction, false);

        return () => {
            document.removeEventListener('keydown', escFunction, false);
        };
    };
};

export const loadingUseEffect = ({
    isCheckinSegment,
    isDeletingSegment,
    isEditingSegment,
    isGeneratingProposal,
    isSavingSegment,
    loadingContext,
}) => {
    return () => {
        if (isEmpty(loadingContext)) return;
        if (
            isCheckinSegment ||
            isDeletingSegment ||
            isEditingSegment ||
            isGeneratingProposal ||
            isSavingSegment
        )
            loadingContext.openLoading(
                i18next.t('Saving change', { count: 2 }).concat('...'),
            );
        else loadingContext.closeLoading();
    };
};

export const editionModeUseEffect = ({
    breakpoint,
    isDisablingModules,
    isEditionMode,
    onOpenDrawer,
    selectedSegmentId,
    tempSegments,
    viewLayersStatus,
    isDisabledEdition,
}) => {
    return () => {
        for (const tempSegmentKey in tempSegments) {
            const tempSegment = tempSegments[tempSegmentKey];
            if (isEditionMode || isDisabledEdition) {
                tempSegment.setDisabled(true);
            } else {
                if (tempSegmentKey === selectedSegmentId) {
                    tempSegment.setDisabled(
                        isEditionMode ||
                            isDisablingModules ||
                            isDisabledEdition,
                        true,
                    );
                } else {
                    tempSegment.setDisabled(
                        isEditionMode || isDisablingModules,
                        false,
                    );
                }
            }

            if (tempSegment.setEnableModulesToEdit) {
                tempSegment.setEnableModulesToEdit(isDisablingModules);
            }

            if (tempSegment.toggleLayers) {
                tempSegment.toggleLayers(viewLayersStatus);
            }
        }
        if (isEditionMode && ['xs', 'sm', 'md'].includes(breakpoint)) {
            onOpenDrawer();
        }
    };
};

export const selectedSegmentIdUseEffect = ({
    segmentsDictionary,
    selectedSegmentId,
    tempSegments,
}) => {
    return () => {
        for (const tempSegmentKey in tempSegments) {
            if (tempSegmentKey === selectedSegmentId) {
                tempSegments[selectedSegmentId].setSelected(true);
            } else {
                tempSegments[tempSegmentKey].setSelected(false, {
                    strokeColor:
                        segmentsDictionary?.[tempSegmentKey]
                            ?.segment_stroke_color,
                });
            }
        }
    };
};
